import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Search, Printer } from "lucide-react";
import { formatCad } from "@/data/catalog";
import { API } from "@/components/JournalParts";
import { ShipDialog } from "@/components/ShipDialog";

const LETTER = { sent: "Letter sent", skipped: "Letter not sent (email not connected)", failed: "Letter failed", pending: "Letter sending…", not_sent: "No letter sent" };
const REVIEW = { sent: "Review request sent", failed: "Review request failed", opted_out: "Customer opted out of review requests", already_reviewed: "Already reviewed, no request needed" };
const reviewLine = (o) => {
  if (o.review_request) return REVIEW[o.review_request.status] || "";
  if (!o.shipped_at) return "";
  const due = new Date(new Date(o.shipped_at).getTime() + 14 * 86400000);
  return `Review request due ${due.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}`;
};

const FILTERS = [["all", "All"], ["to_ship", "To ship"], ["shipped", "Shipped"]];
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "");

const OrderRow = ({ o, onShip, onSlip }) => {
  const [open, setOpen] = useState(false);
  const a = o.address || {};
  const s = o.shipment;
  return (
    <li className="border-b border-[#303B2D]/60" data-testid={`order-row-${o.reference}`}>
      <div className="py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <button data-testid={`order-toggle-${o.reference}`} onClick={() => setOpen(!open)} className="font-ledger text-[13px] text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
          {o.reference}
        </button>
        <span className="font-ledger text-[11px] text-[#D3CEC4]/45 w-24">{day(o.paid_at)}</span>
        <span className="text-[14px] text-[#E8DDC7] min-w-[140px]">{o.name || "No name"}</span>
        <span className="text-[12px] text-[#D3CEC4]/55 flex-1 min-w-[160px]">
          {(o.items || []).map((i) => `${i.qty} × ${i.name}`).join(", ")}
        </span>
        <span className="font-ledger text-[13px] text-[#E8DDC7]">{formatCad(o.total)}</span>
        {o.mode === "subscription" && <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A96B]">Standing</span>}
        {o.refunded && <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9876B]">Refunded</span>}
        <button data-testid={`order-slip-${o.reference}`} onClick={() => onSlip(o)} title="Packing slip (4×6)"
          className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#D3CEC4]/55 hover:text-[#B5A642] transition-colors">
          <Printer className="w-3.5 h-3.5" /> Slip
        </button>
        <label className="inline-flex items-center gap-2 cursor-pointer text-[11px] uppercase tracking-[0.18em] text-[#D3CEC4]/60">
          <input type="checkbox" data-testid={`order-shipped-${o.reference}`} checked={o.shipped} onChange={(e) => onShip(o, e.target.checked)} className="accent-[#8FB08A] w-4 h-4" />
          <span className={o.shipped ? "text-[#8FB08A]" : ""}>{o.shipped ? `Shipped ${day(o.shipped_at)}` : "Shipped"}</span>
        </label>
      </div>
      {open && (
        <div className="pb-5 grid md:grid-cols-3 gap-6 text-[13px] text-[#D3CEC4]/75" data-testid={`order-detail-${o.reference}`}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/40 mb-1">Ship to</p>
            <p>{o.name}</p><p>{a.line1}</p>{a.line2 && <p>{a.line2}</p>}
            <p>{[a.city, a.state, a.postal_code].filter(Boolean).join(", ")}</p>
            <p className="mt-2 text-[#D3CEC4]/55">{o.email}{o.phone && ` · ${o.phone}`}</p>
            {o.shipped && s && (
              <div className="mt-4" data-testid={`order-shipment-${o.reference}`}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/40 mb-1">Shipment</p>
                {s.tracking ? (
                  <p>{s.carrier_label} · {s.tracking_url
                    ? <a href={s.tracking_url} target="_blank" rel="noreferrer" className="font-ledger text-[#B5A642] hover:text-[#E8DDC7]">{s.tracking}</a>
                    : <span className="font-ledger">{s.tracking}</span>}</p>
                ) : <p className="text-[#D3CEC4]/50">No tracking number</p>}
                <p className="text-[12px] text-[#D3CEC4]/50" data-testid={`order-letter-status-${o.reference}`}>{LETTER[s.letter_status] || ""}</p>
                <p className="text-[12px] text-[#D3CEC4]/50" data-testid={`order-review-request-${o.reference}`}>{reviewLine(o)}</p>
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/40 mb-1">Items</p>
            {(o.items || []).map((i) => (
              <p key={i.name} className="flex justify-between gap-4"><span>{i.qty} × {i.name}</span><span className="font-ledger">{formatCad(i.amount)}</span></p>
            ))}
            {o.dog_weight_lb && <p className="mt-2 text-[#D3CEC4]/50">Dog's weight: {o.dog_weight_lb} lb</p>}
          </div>
          <div className="font-ledger space-y-0.5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/40 mb-1 font-sans">Totals</p>
            <p className="flex justify-between"><span>Subtotal</span><span>{formatCad(o.subtotal)}</span></p>
            {o.discount > 0 && <p className="flex justify-between"><span>Discount</span><span>−{formatCad(o.discount)}</span></p>}
            <p className="flex justify-between"><span>Shipping{o.welcome_code && ` (${o.welcome_code})`}</span><span>{formatCad(o.shipping)}</span></p>
            <p className="flex justify-between"><span>Tax</span><span>{formatCad(o.tax)}</span></p>
            <p className="flex justify-between text-[#E8DDC7]"><span>Total</span><span>{formatCad(o.total)}</span></p>
          </div>
        </div>
      )}
    </li>
  );
};

export const KeeperOrders = ({ token, initialFilter = "all", onChange }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [shipping, setShipping] = useState(null);

  const load = () => axios.get(`${API}/keeper/orders`, auth).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (o, body) => {
    try {
      await axios.post(`${API}/keeper/orders/${o.session_id}/shipped`, body, auth);
    } finally {
      load();
      onChange?.();
      if (body.send_letter) setTimeout(load, 4000);
    }
  };
  const onShip = (o, shipped) => (shipped ? setShipping(o) : save(o, { shipped: false }));
  const confirmShip = async (extra) => {
    await save(shipping, { shipped: true, ...extra });
    setShipping(null);
  };

  const onSlip = async (o) => {
    const win = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/keeper/orders/${o.session_id}/slip.pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url;
      else Object.assign(document.createElement("a"), { href: url, download: `packing-slip-${o.reference}.pdf` }).click();
    } catch {
      win?.close();
      window.alert("The packing slip couldn't be prepared. Please try again.");
    }
  };

  const downloadCsv = async () => {
    const { data } = await axios.get(`${API}/keeper/orders.csv`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(data);
    Object.assign(document.createElement("a"), { href: url, download: "apothecary-orders.csv" }).click();
    URL.revokeObjectURL(url);
  };

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (orders || []).filter((o) =>
      (filter === "all" || (filter === "shipped" ? o.shipped : !o.shipped && !o.refunded)) &&
      (!s || [o.reference, o.name, o.email, o.address?.city].some((v) => (v || "").toLowerCase().includes(s)))
    );
  }, [orders, q, filter]);

  const toShip = (orders || []).filter((o) => !o.shipped && !o.refunded).length;

  return (
    <div className="mt-6" data-testid="keeper-orders">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#D3CEC4]/40" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, city or reference"
            className="w-full bg-transparent border border-[#303B2D] rounded-sm pl-9 pr-3 py-2 text-[14px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70" />
        </div>
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`orders-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] rounded-sm border transition-colors ${filter === k ? "border-[#B5A642] text-[#B5A642]" : "border-[#303B2D] text-[#D3CEC4]/55 hover:text-[#E8DDC7]"}`}>
            {label}{k === "to_ship" && ` · ${toShip}`}
          </button>
        ))}
        <button data-testid="orders-download-csv" onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 border border-[#B5A642]/60 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4] rounded-sm hover:bg-[#B5A642]/10 transition-colors">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </div>
      {orders === null && <p className="mt-8 text-[#D3CEC4]/50">Opening the ledger…</p>}
      {orders && shown.length === 0 && <p data-testid="orders-empty" className="mt-8 font-display italic text-[#D3CEC4]/50">No orders match.</p>}
      <ul className="mt-4">{shown.map((o) => <OrderRow key={o.session_id} o={o} onShip={onShip} onSlip={onSlip} />)}</ul>
      {shipping && <ShipDialog order={shipping} onCancel={() => setShipping(null)} onConfirm={confirmShip} />}
    </div>
  );
};
